// The engine's line from a position, played as far as its point is made:
// what the puzzle's Show solution plays, and what the step controls' Main
// line button plays at a position where the reader's next move was a
// mistake (moved here from review-puzzles' solution-line.ts, 2026-10-14).
//
// Where it stops. The scan carries one score per line, at its end, and none
// per ply, so the measure along the line is material: the side's lead in
// pawns over the lead it had at the start (`balance`).
// - A line that mates plays to the mate (`mate`).
// - Otherwise the line plays to the first ply after which the side's gain
//   reaches `target` and holds it for the rest of the line (`gain`): an
//   exchange in progress is played through, so the line never stops on a
//   capture about to be recaptured. A target of 'mate' (the line's score is
//   a mate) skips this: the line plays to its end or the mate.
// - A line that never realises its gain plays to its end (`end`).
// At least the first move, when it is legal from the position.
import type { Chess } from '@chess/api-chess';
import { uciToInput } from '@chess/api-chess';
import type { Color, Fen, San, Uci } from '@chess/api-model';
import { balance } from './material.ts';

export type MainLineReason = 'mate' | 'gain' | 'end';

export interface MainLine {
  /** The moves to play, both sides, the side's first. */
  moves: Uci[];
  /** The same moves in SAN, for a status line. */
  sans: San[];
  reason: MainLineReason;
}

/** The rules of chess the line needs: to play a move and read the position after it. */
export type MainLineRules = Pick<Chess, 'play'>;

export interface MainLineInput {
  /** The position the line starts from. */
  fen: Fen;
  /** Whose gain is measured: the side to move at `fen`. */
  side: Color;
  /** The engine's line from `fen`. */
  line: Uci[];
  /** The gain in pawns that makes the line's point, or 'mate' when the line's score is a mate. */
  target: number | 'mate';
}

export function mainLineOf({ fen: start, side, line, target }: MainLineInput, chess: MainLineRules): MainLine {
  const moves: Uci[] = [];
  const sans: San[] = [];
  const gains: number[] = [];
  const opening = balance(start, side);
  let fen = start;
  let mated = false;
  for (const uci of line) {
    const played = chess.play(fen, uciToInput(uci));
    if (!played) break;
    moves.push(uci);
    sans.push(played.move.san);
    fen = played.fen;
    gains.push(balance(fen, side) - opening);
    if (played.move.flags.checkmate) {
      mated = true;
      break;
    }
  }
  if (mated) return { moves, sans, reason: 'mate' };
  if (target !== 'mate') {
    for (let i = 0; i < gains.length; i++) {
      if (gains.slice(i).every((g) => g >= target)) return { moves: moves.slice(0, i + 1), sans: sans.slice(0, i + 1), reason: 'gain' };
    }
  }
  return { moves, sans, reason: 'end' };
}
